'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useAfterPaint } from '@/src/lib/hooks/ui/useAfterPaint';
import { MapPin } from 'lucide-react';
import { css, cx } from '@/styled-system/css';
import { LucideIcon } from '@/src/components/LucideIcon';
import type { AreaResponse, AutocompleteResult } from '@/src/lib/api';
import LocationSearchModal from './LocationSearchModal';
import {
  locationPillLayout,
  locationPillPlaceholder,
  locationPillSurface,
  locationPillTapArea,
  locationPillValue,
} from './locationPill.styles';
import { formatAreaLabel, type LocationAnalyticsSurface } from './useLocationSuggestions';

// The pill's shape, fill, layout, tap area and text weights are shared with the dialog field.
const triggerButton = css({
  cursor: 'pointer',
  textAlign: 'start',
  // Constellation's own focus treatment, not the browser's default outline —
  // otherwise this field focuses blue while the combobox next to it focuses
  // teal. `shadows.focus` is the same token c11n's own controls use.
  _focusVisible: {
    outline: 'none',
    boxShadow: 'focus',
  },
  // Also under focus-within: Android focuses a tapped button, and the pill pins its fill there.
  _pressed: {
    backgroundColor: 'bg.action.neutral.alt.pressed',
    _focusWithin: { backgroundColor: 'bg.action.neutral.alt.pressed' },
  },
});

const label = css({
  textStyle: 'body',
  color: 'text.neutral',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const srOnly = css({ srOnly: true });
const pillSurface = css(locationPillSurface);
const valueText = css(locationPillValue);
const placeholderText = css(locationPillPlaceholder);

/** Mirrors `LocationCombobox`, so the bar can swap one for the other. */
export type LocationInputProps = {
  /** Currently selected area, shown as the field's value. */
  area: AreaResponse | AutocompleteResult | null;
  /** IP-derived city for the "Near Me" affordance in the dialog. */
  nearMeArea?: AreaResponse | null;
  /** Called when the user picks a suggestion or "Near Me". */
  onSelect: (result: AutocompleteResult) => void;
  /** Called when Enter should run the search for a suggestion right away. */
  onEnterSearch: (result: AutocompleteResult) => void;
  /** Called on every keystroke so the parent can cancel recent-search seeding. */
  onUserInput: () => void;
  size?: 'sm' | 'lg';
  analyticsSurface?: LocationAnalyticsSurface;
  /**
   * Accepted for prop parity with `LocationCombobox` (the bar spreads one
   * props object into whichever field it renders), but unused here: both SRP
   * bars take the same leading inset, and only the Combobox has a clear
   * control whose spacing compact has to correct.
   */
  compact?: boolean;
  testIdSuffix?: string;
};

/**
 * The location field below `lg`: a button that opens the full-screen search
 * (HP-8998), styled to match the Combobox trigger it stands in for.
 *
 * Deliberately not a Combobox. Tapping this opens a dialog, so the element is a
 * button with `aria-haspopup="dialog"` — not an input reporting
 * `role="combobox"` for a listbox it will never show. It also means no
 * floating-ui, no suggestion fetching and no options work runs in the bar at
 * all; that lives in the dialog, which only mounts once opened.
 */
export default function LocationInput({
  area,
  nearMeArea,
  onSelect,
  onEnterSearch,
  onUserInput,
  size = 'sm',
  analyticsSurface = size === 'lg' ? 'homepage' : 'search',
  testIdSuffix,
}: LocationInputProps) {
  const tSearch = useTranslations('search');
  const areaLabel = formatAreaLabel(area);
  const triggerCss = cx(
    pillSurface,
    locationPillLayout({ inset: size === 'lg' ? 'both' : 'start' }),
    locationPillTapArea,
    triggerButton,
  );

  // The dialog is this field's own popup, the way the dropdown belongs to
  // LocationCombobox — the bar shouldn't have to know either exists. Mounted
  // only while open so dismissing discards its draft query.
  const [isOpen, setIsOpen] = useState(false);
  const close = useCallback(() => setIsOpen(false), []);
  // HP-9442: paint the pressed pill before the dialog mounts.
  const { run: open, isPending: isOpening } = useAfterPaint(() => setIsOpen(true), { trackPending: true });

  return (
    <>
      <button
        // Inside ListingSearch's <form>, so it must not be the submit button.
        type="button"
        className={triggerCss}
        onClick={() => void open()}
        aria-haspopup="dialog"
        data-pressed={isOpening ? '' : undefined}
        data-testid={testIdSuffix ? `location-search-trigger-${testIdSuffix}` : 'location-search-trigger'}
      >
        <LucideIcon icon={MapPin} size={size === 'lg' ? 'md' : 'sm'} />
        {/* Named by its content, not `aria-label` — that would replace the
            visible text, so the accessible name would never carry the selected
            area (WCAG 2.5.3 Label in Name, and the Combobox this stands in for
            announced its value). */}
        <span className={srOnly}>{tSearch('searchByLocation')}</span>
        <span className={cx(label, areaLabel ? valueText : placeholderText)}>
          {areaLabel || tSearch('findApartmentsIn')}
        </span>
      </button>
      {isOpen ? (
        <LocationSearchModal
          onClose={close}
          currentResourceId={area?.resourceId}
          nearMeArea={nearMeArea}
          onSelect={onSelect}
          onEnterSearch={onEnterSearch}
          onUserInput={onUserInput}
          analyticsSurface={analyticsSurface}
        />
      ) : null}
    </>
  );
}
